// src/pages/Templates/Template-Administrativo/Coordenacao/Standby/StandbyDetailSidePanel.tsx

import React, { useEffect, useMemo } from "react";

type TipoStandby = "servico" | "venda";
type AcaoStandby = "retornar" | "baixar";

type DetailInfoItem = {
  label: string;
  value: string;
};

type QuantityDialogState = {
  acao: AcaoStandby;
  modo: "item" | "todos";
  metas: any[];
  titulo: string;
  descricao: string;
  quantidadeMaxima: number;
};

type StandbyDetailSidePanelProps = {
  open: boolean;
  referencia: string | null;
  tipo: TipoStandby;

  modalInfo: DetailInfoItem[];
  modalHeaders: string[];
  modalColWidths: string[];
  modalTableRows: string[][];

  selectedIndex: number | null;
  submittingAction: AcaoStandby | null;
  actionError: string | null;

  permissoesStandby: {
    podeGerarRelatorio: boolean;
    podeRetornar: boolean;
    podeBaixar: boolean;
  };

  podeBaixarReferenciaAberta: boolean;
  selectedMetaParaBaixa: any | null;
  modalMetas: any[];
  modalMetasParaBaixa: any[];
  modalRawMovs: any[];

  quantityDialog: QuantityDialogState | null;
  quantityInput: string;
  quantityError: string | null;

  onClose: () => void;
  onSelectIndex: (index: number | null) => void;

  onGerarRelatorioCompleto: () => void;
  onGerarRelatorioItem: () => void;

  onAbrirPopupQuantidade: (acao: AcaoStandby, modo: "item" | "todos") => void;
  onCancelarPopupQuantidade: () => void;
  onConfirmarMovimentacao: () => void;

  onQuantityInputChange: (value: string) => void;
  onClearQuantityError: () => void;
};

const HEADER_HEIGHT = 52;
const LEFTBAR_WIDTH = 64;
const MOBILE_BREAKPOINT = 900;
const PRIMARY_COLOR = "#0d343d";
const RETURN_COLOR = "#d97706";
const DANGER_COLOR = "#b91c1c";

const STANDBY_DETAIL_SIDE_PANEL_CSS = `
.standby-detail-backdrop {
  position: fixed;
  top: ${HEADER_HEIGHT}px;
  left: ${LEFTBAR_WIDTH}px;
  right: 0;
  bottom: 0;
  z-index: 1190;
  display: flex;
  justify-content: flex-end;
  background:
    radial-gradient(circle at top left, rgba(13, 52, 61, 0.12), transparent 34%),
    rgba(15, 23, 42, 0.28);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  box-sizing: border-box;
}

.standby-detail-panel {
  width: min(1120px, calc(100vw - ${LEFTBAR_WIDTH}px));
  height: calc(100vh - ${HEADER_HEIGHT}px);
  background: #ffffff;
  border-left: 1px solid #d9e2ec;
  box-shadow: -18px 0 46px rgba(15, 23, 42, 0.22);
  display: flex;
  flex-direction: column;
  font-family:
    Nunito,
    Inter,
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  color: #0f172a;
}

.standby-detail-header {
  padding: 18px 20px 16px;
  border-bottom: 1px solid #e5eaf0;
  background:
    linear-gradient(135deg, rgba(248, 250, 252, 0.98), rgba(255, 255, 255, 1)),
    #ffffff;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.standby-detail-header-text {
  min-width: 0;
}

.standby-detail-eyebrow {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.42px;
  text-transform: uppercase;
}

.standby-detail-title {
  margin: 6px 0 4px;
  color: #0f172a;
  font-size: 21px;
  font-weight: 950;
  line-height: 1.18;
  letter-spacing: -0.02em;
  overflow-wrap: anywhere;
}

.standby-detail-subtitle {
  margin: 0;
  max-width: 780px;
  color: #64748b;
  font-size: 12.5px;
  font-weight: 760;
  line-height: 1.45;
}

.standby-detail-close-button {
  width: 36px;
  height: 36px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #334155;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    color 0.16s ease,
    transform 0.16s ease;
}

.standby-detail-close-button:hover {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateY(-1px);
}

.standby-detail-close-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.standby-detail-content {
  flex: 1;
  overflow-y: auto;
  background:
    linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.standby-detail-section {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  padding: 13px;
  display: flex;
  flex-direction: column;
  gap: 11px;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.03);
}

.standby-detail-section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.standby-detail-section-title-block {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.standby-detail-section-title-block strong {
  color: #0f172a;
  font-size: 13.5px;
  font-weight: 950;
}

.standby-detail-section-title-block span {
  color: #64748b;
  font-size: 12px;
  font-weight: 760;
  line-height: 1.35;
}

.standby-detail-info-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.standby-detail-info-box {
  border: 1px solid #edf2f7;
  background: #f8fafc;
  padding: 9px 10px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.standby-detail-info-box.is-wide {
  grid-column: span 2;
}

.standby-detail-info-label {
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
}

.standby-detail-info-value {
  color: #0f172a;
  font-size: 12.5px;
  font-weight: 900;
  line-height: 1.35;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.standby-detail-error {
  padding: 9px 10px;
  border: 1px solid #fecaca;
  background: #fffafa;
  color: #991b1b;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.38;
}

.standby-detail-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.standby-detail-action-button {
  min-height: 36px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #334155;
  padding: 0 13px;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    transform 0.16s ease,
    opacity 0.16s ease,
    box-shadow 0.16s ease;
}

.standby-detail-action-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

.standby-detail-action-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.standby-detail-action-button.is-primary {
  background: ${PRIMARY_COLOR};
  border-color: ${PRIMARY_COLOR};
  color: #ffffff;
}

.standby-detail-action-button.is-danger {
  background: #ffffff;
  border-color: ${DANGER_COLOR};
  color: ${DANGER_COLOR};
}

.standby-detail-action-button.is-danger:hover:not(:disabled) {
  background: #fffafa;
}

.standby-detail-action-button.is-return {
  background: #ffffff;
  border-color: ${RETURN_COLOR};
  color: ${RETURN_COLOR};
}

.standby-detail-action-button.is-return:hover:not(:disabled) {
  background: #fffaf2;
}

.standby-detail-help-text {
  margin: 0;
  color: #64748b;
  font-size: 12px;
  font-weight: 760;
  line-height: 1.42;
}

.standby-detail-table-shell {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  padding: 12px;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.03);
}

.standby-detail-table-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.standby-detail-table-title {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.standby-detail-table-title strong {
  color: #0f172a;
  font-size: 13.5px;
  font-weight: 950;
}

.standby-detail-table-title span {
  color: #64748b;
  font-size: 12px;
  font-weight: 760;
}

.standby-detail-table-scroll {
  width: 100%;
  overflow-x: auto;
  font-family: Arial, sans-serif;
  border: 1px solid #d7dee2;
  background: #ffffff;
}

.standby-detail-table-inner {
  min-width: 1100px;
}

.standby-detail-table-row {
  display: grid;
}

.standby-detail-table-head {
  font-weight: 700;
  padding: 10px;
  border-bottom: 1px solid ${PRIMARY_COLOR};
  background: ${PRIMARY_COLOR};
  color: #ffffff;
  font-family: Arial, sans-serif;
  font-size: 12px;
}

.standby-detail-table-body-row {
  padding: 12px 10px;
  border-bottom: 1px solid #e7ecef;
  background: #ffffff;
  cursor: pointer;
  user-select: none;
  font-family: Arial, sans-serif;
  font-size: 12px;
}

.standby-detail-table-body-row:hover {
  background: rgba(13, 52, 61, 0.045);
}

.standby-detail-table-body-row.is-selected {
  background: rgba(13, 52, 61, 0.08);
}

.standby-detail-table-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #263238;
  min-width: 0;
}

.standby-detail-table-body-row.is-selected .standby-detail-table-cell {
  color: ${PRIMARY_COLOR};
  font-weight: 700;
}

.standby-detail-empty {
  border: 1px dashed #cbd5e1;
  background: #ffffff;
  padding: 14px;
  color: #64748b;
  font-size: 12.5px;
  font-weight: 850;
}

.standby-detail-operation-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.standby-detail-operation-card {
  position: relative;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 11px;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.03);
  overflow: hidden;
}

.standby-detail-operation-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: #cbd5e1;
}

.standby-detail-operation-card.is-return {
  border-color: rgba(217, 119, 6, 0.46);
}

.standby-detail-operation-card.is-return::before {
  background: ${RETURN_COLOR};
}

.standby-detail-operation-card.is-danger {
  border-color: rgba(185, 28, 28, 0.42);
}

.standby-detail-operation-card.is-danger::before {
  background: ${DANGER_COLOR};
}

.standby-detail-operation-title {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding-left: 2px;
}

.standby-detail-operation-title strong {
  color: #0f172a;
  font-size: 13.5px;
  font-weight: 950;
}

.standby-detail-operation-title span {
  color: #64748b;
  font-size: 12px;
  font-weight: 760;
  line-height: 1.35;
}

.standby-detail-operation-card.is-return .standby-detail-operation-title strong {
  color: #7c3f00;
}

.standby-detail-operation-card.is-danger .standby-detail-operation-title strong {
  color: #7f1d1d;
}

.standby-detail-operation-disabled {
  border: 1px dashed #cbd5e1;
  background: #f8fafc;
  color: #64748b;
  padding: 10px;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.35;
}

.standby-quantity-backdrop {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(15, 23, 42, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
}

.standby-quantity-modal {
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border: 1px solid #cfd8dc;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.22);
  font-family: Arial, sans-serif;
}

.standby-quantity-header {
  background: ${PRIMARY_COLOR};
  color: #ffffff;
  padding: 14px 16px;
  font-size: 15px;
  font-weight: 700;
}

.standby-quantity-body {
  padding: 16px;
  display: grid;
  gap: 12px;
}

.standby-quantity-description {
  font-size: 13px;
  color: #334155;
  line-height: 1.45;
}

.standby-quantity-field {
  display: grid;
  gap: 5px;
}

.standby-quantity-label {
  font-size: 12px;
  color: ${PRIMARY_COLOR};
  font-weight: 700;
}

.standby-quantity-input {
  height: 38px;
  border: 1px solid #b9c5ca;
  outline: none;
  padding: 0 10px;
  font-size: 14px;
  color: #111827;
  box-sizing: border-box;
}

.standby-quantity-input:focus {
  border-color: ${PRIMARY_COLOR};
  box-shadow: 0 0 0 2px rgba(13, 52, 61, 0.12);
}

.standby-quantity-max {
  font-size: 11.5px;
  color: #64748b;
}

.standby-quantity-error {
  padding: 8px 9px;
  border: 1px solid #fecaca;
  background: #fffafa;
  color: #991b1b;
  font-size: 12px;
}

.standby-quantity-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}

@media (max-width: ${MOBILE_BREAKPOINT}px) {
  .standby-detail-backdrop {
    top: 0;
    left: 0;
  }

  .standby-detail-panel {
    width: 100vw;
    height: 100vh;
  }

  .standby-detail-info-grid,
  .standby-detail-operation-grid {
    grid-template-columns: 1fr;
  }

  .standby-detail-info-box.is-wide {
    grid-column: auto;
  }

  .standby-detail-section-header,
  .standby-detail-table-header {
    flex-direction: column;
  }
}
`;

function normalizeInfoLabel(label: string): string {
  return String(label || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function isWideInfo(label: string): boolean {
  const normalized = normalizeInfoLabel(label);

  return (
    normalized.includes("material") ||
    normalized.includes("custo") ||
    normalized.includes("comentario") ||
    normalized.includes("observacao")
  );
}

function getCenterCols(tipo: TipoStandby): number[] {
  if (tipo === "venda") {
    return [0, 1, 3, 4, 5, 7, 8];
  }

  return [1, 3, 4, 5, 7, 8];
}

const DetailInfoBox: React.FC<{ item: DetailInfoItem }> = ({ item }) => {
  return (
    <div
      className={`standby-detail-info-box ${
        isWideInfo(item.label) ? "is-wide" : ""
      }`}
    >
      <div className="standby-detail-info-label">{item.label}</div>
      <div className="standby-detail-info-value">{item.value || "—"}</div>
    </div>
  );
};

const StandbyDetailSidePanel: React.FC<StandbyDetailSidePanelProps> = ({
  open,
  referencia,
  tipo,
  modalInfo,
  modalHeaders,
  modalColWidths,
  modalTableRows,
  selectedIndex,
  submittingAction,
  actionError,
  permissoesStandby,
  podeBaixarReferenciaAberta,
  selectedMetaParaBaixa,
  modalMetas,
  modalMetasParaBaixa,
  modalRawMovs,
  quantityDialog,
  quantityInput,
  quantityError,
  onClose,
  onSelectIndex,
  onGerarRelatorioCompleto,
  onGerarRelatorioItem,
  onAbrirPopupQuantidade,
  onCancelarPopupQuantidade,
  onConfirmarMovimentacao,
  onQuantityInputChange,
  onClearQuantityError,
}) => {
  const refLimpa = useMemo(
    () => String(referencia || "").trim(),
    [referencia]
  );

  const refLabel = tipo === "venda" ? "P.V." : "O.S.";
  const totalItens = modalTableRows.length;
  const centerCols = useMemo(() => getCenterCols(tipo), [tipo]);

  const gridTemplateColumns = useMemo(() => {
    if (modalColWidths.length > 0) {
      return modalColWidths.join(" ");
    }

    return `repeat(${Math.max(modalHeaders.length, 1)}, minmax(110px, 1fr))`;
  }, [modalColWidths, modalHeaders.length]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (quantityDialog) {
          onCancelarPopupQuantidade();
          return;
        }

        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, quantityDialog, onClose, onCancelarPopupQuantidade]);

  if (!open) return null;

  return (
    <>
      <style>{STANDBY_DETAIL_SIDE_PANEL_CSS}</style>

      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Detalhes da ${refLabel} ${refLimpa || ""}`}
        className="standby-detail-backdrop"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            onClose();
          }
        }}
      >
        <aside
          className="standby-detail-panel"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <header className="standby-detail-header">
            <div className="standby-detail-header-text">
              <div className="standby-detail-eyebrow">
                Coordenação · Standby de materiais
              </div>

              <h2 className="standby-detail-title">
                {refLabel} {refLimpa || "—"}
              </h2>

              <p className="standby-detail-subtitle">
                Detalhamento operacional dos materiais em standby, com geração
                de relatório e ações de retorno ou baixa conforme permissão do
                departamento.
              </p>
            </div>

            <button
              type="button"
              className="standby-detail-close-button"
              onClick={onClose}
              aria-label={`Fechar detalhes da ${refLabel}`}
              disabled={!!submittingAction}
            >
              ×
            </button>
          </header>

          <div className="standby-detail-content">
            <section className="standby-detail-section">
              <div className="standby-detail-section-header">
                <div className="standby-detail-section-title-block">
                  <strong>Dados principais da {refLabel}</strong>
                  <span>
                    Informações consolidadas da movimentação e do contexto de
                    atendimento.
                  </span>
                </div>
              </div>

              {modalInfo.length > 0 ? (
                <div className="standby-detail-info-grid">
                  {modalInfo.map((item) => (
                    <DetailInfoBox
                      key={`${item.label}-${item.value}`}
                      item={item}
                    />
                  ))}
                </div>
              ) : (
                <div className="standby-detail-empty">
                  Nenhuma informação consolidada disponível.
                </div>
              )}
            </section>

            {actionError && (
              <div className="standby-detail-error">{actionError}</div>
            )}

            <section className="standby-detail-section">
              <div className="standby-detail-section-header">
                <div className="standby-detail-section-title-block">
                  <strong>Relatórios</strong>
                  <span>
                    Gere o relatório completo da referência ou apenas do item
                    selecionado na listagem.
                  </span>
                </div>

                <div className="standby-detail-actions">
                  {permissoesStandby.podeGerarRelatorio && (
                    <button
                      type="button"
                      className="standby-detail-action-button is-primary"
                      disabled={!modalRawMovs.length || !!submittingAction}
                      onClick={onGerarRelatorioCompleto}
                    >
                      {tipo === "venda"
                        ? "Gerar relatório da P.V."
                        : "Gerar relatório da O.S."}
                    </button>
                  )}

                  {permissoesStandby.podeGerarRelatorio && (
                    <button
                      type="button"
                      className="standby-detail-action-button"
                      disabled={selectedIndex == null || !!submittingAction}
                      onClick={onGerarRelatorioItem}
                    >
                      Gerar relatório do item
                    </button>
                  )}
                </div>
              </div>
            </section>

            <section className="standby-detail-table-shell">
              <div className="standby-detail-table-header">
                <div className="standby-detail-table-title">
                  <strong>Itens em standby</strong>
                  <span>
                    {totalItens} item(ns) vinculado(s) à {refLabel}{" "}
                    {refLimpa || "—"}. Selecione um item para ações parciais.
                  </span>
                </div>
              </div>

              {modalTableRows.length > 0 ? (
                <div className="standby-detail-table-scroll">
                  <div
                    className="standby-detail-table-inner"
                    style={{
                      minWidth: Math.max(1100, modalHeaders.length * 120),
                    }}
                  >
                    <div
                      className="standby-detail-table-row standby-detail-table-head"
                      style={{ gridTemplateColumns }}
                    >
                      {modalHeaders.map((header, index) => (
                        <div
                          key={`${header}-${index}`}
                          className="standby-detail-table-cell"
                          style={{
                            color: "#ffffff",
                            fontWeight: 700,
                            textAlign: centerCols.includes(index)
                              ? "center"
                              : "left",
                          }}
                          title={header}
                        >
                          {header}
                        </div>
                      ))}
                    </div>

                    {modalTableRows.map((row, rowIndex) => {
                      const selected = rowIndex === selectedIndex;

                      return (
                        <div
                          key={`${refLimpa}-${rowIndex}`}
                          className={`standby-detail-table-row standby-detail-table-body-row ${
                            selected ? "is-selected" : ""
                          }`}
                          style={{ gridTemplateColumns }}
                          onClick={() => {
                            onSelectIndex(rowIndex);
                          }}
                          title="Clique para selecionar"
                        >
                          {row.map((cell, cellIndex) => (
                            <div
                              key={`${rowIndex}-${cellIndex}`}
                              className="standby-detail-table-cell"
                              style={{
                                textAlign: centerCols.includes(cellIndex)
                                  ? "center"
                                  : "left",
                              }}
                              title={String(cell ?? "")}
                            >
                              {cell}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="standby-detail-empty">
                  Nenhum item localizado para esta referência.
                </div>
              )}
            </section>

            <section className="standby-detail-operation-grid">
              <div className="standby-detail-operation-card is-return">
                <div className="standby-detail-operation-title">
                  <strong>Opções de retorno</strong>
                  <span>
                    Use quando o material precisa voltar ao estoque. Disponível
                    para Suprimentos e Diretoria.
                  </span>
                </div>

                {permissoesStandby.podeRetornar ? (
                  <div className="standby-detail-actions">
                    <button
                      type="button"
                      className="standby-detail-action-button is-return"
                      disabled={selectedIndex == null || !!submittingAction}
                      onClick={() => onAbrirPopupQuantidade("retornar", "item")}
                    >
                      Retornar produto ao estoque
                    </button>

                    <button
                      type="button"
                      className="standby-detail-action-button is-return"
                      disabled={!modalMetas.length || !!submittingAction}
                      onClick={() =>
                        onAbrirPopupQuantidade("retornar", "todos")
                      }
                    >
                      {tipo === "venda"
                        ? "Retornar P.V. ao estoque"
                        : "Retornar O.S. ao estoque"}
                    </button>
                  </div>
                ) : (
                  <div className="standby-detail-operation-disabled">
                    Seu departamento não possui permissão para retornar materiais
                    ao estoque.
                  </div>
                )}

                <p className="standby-detail-help-text">
                  Para retorno parcial, selecione um item na listagem. Para
                  retorno total, use a ação da referência inteira.
                </p>
              </div>

              <div className="standby-detail-operation-card is-danger">
                <div className="standby-detail-operation-title">
                  <strong>Opções de baixa</strong>
                  <span>
                    Use quando o material foi consumido, aplicado no serviço ou
                    não deve retornar ao estoque.
                  </span>
                </div>

                {podeBaixarReferenciaAberta ? (
                  <div className="standby-detail-actions">
                    <button
                      type="button"
                      className="standby-detail-action-button is-danger"
                      disabled={!selectedMetaParaBaixa || !!submittingAction}
                      onClick={() => onAbrirPopupQuantidade("baixar", "item")}
                    >
                      Realizar baixa do produto
                    </button>

                    <button
                      type="button"
                      className="standby-detail-action-button is-danger"
                      disabled={
                        !modalMetasParaBaixa.length || !!submittingAction
                      }
                      onClick={() => onAbrirPopupQuantidade("baixar", "todos")}
                    >
                      {tipo === "venda"
                        ? "Realizar baixa da P.V."
                        : "Realizar baixa da O.S."}
                    </button>
                  </div>
                ) : (
                  <div className="standby-detail-operation-disabled">
                    Baixa indisponível para esta referência. Coordenação só pode
                    baixar O.S. vinculada ao próprio usuário; Diretoria possui
                    acesso ampliado.
                  </div>
                )}

                <p className="standby-detail-help-text">
                  Para baixa parcial, selecione um item na listagem. Para baixa
                  total, use a ação da referência inteira.
                </p>
              </div>
            </section>
          </div>
        </aside>
      </div>

      {quantityDialog && (
        <div
          className="standby-quantity-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={quantityDialog.titulo}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              onCancelarPopupQuantidade();
            }
          }}
        >
          <div
            className="standby-quantity-modal"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="standby-quantity-header">
              {quantityDialog.titulo}
            </div>

            <div className="standby-quantity-body">
              <div className="standby-quantity-description">
                {quantityDialog.descricao}
              </div>

              <div className="standby-quantity-field">
                <label className="standby-quantity-label">
                  Quantidade a movimentar
                </label>

                <input
                  autoFocus
                  type="number"
                  min="1"
                  step="1"
                  max={quantityDialog.quantidadeMaxima}
                  value={quantityInput}
                  onChange={(event) => {
                    onQuantityInputChange(event.target.value);
                    onClearQuantityError();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      onConfirmarMovimentacao();
                    }

                    if (event.key === "Escape") {
                      onCancelarPopupQuantidade();
                    }
                  }}
                  className="standby-quantity-input"
                />

                <div className="standby-quantity-max">
                  Máximo permitido:{" "}
                  <strong>{quantityDialog.quantidadeMaxima}</strong>
                </div>
              </div>

              {quantityError && (
                <div className="standby-quantity-error">{quantityError}</div>
              )}

              <div className="standby-quantity-actions">
                <button
                  type="button"
                  disabled={!!submittingAction}
                  onClick={onCancelarPopupQuantidade}
                  className="standby-detail-action-button"
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  disabled={!!submittingAction}
                  onClick={onConfirmarMovimentacao}
                  className="standby-detail-action-button is-primary"
                >
                  {submittingAction
                    ? "Registrando..."
                    : quantityDialog.acao === "retornar"
                    ? "Confirmar retorno"
                    : "Confirmar baixa"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StandbyDetailSidePanel;